import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { DocumentDTO } from '@propmgt/shared';
import { api, errorMessage } from '../../api/client';
import { formatDate } from '../../lib/date';
import { ErrorState, Loading, StatusMessage } from '../../components/ui/Feedback';

export function DocumentsView({ documents }: { documents: DocumentDTO[] }) {
  const [msg, setMsg] = useState<string | null>(null);

  async function download(id: string): Promise<void> {
    setMsg(null);
    try {
      const res = await api.documents.signedUrl(id);
      window.open(res.url, '_blank', 'noopener');
    } catch (err) {
      setMsg(errorMessage(err));
    }
  }

  return (
    <>
      <h1>Your documents</h1>
      <p className="subtitle">Leases, receipts, notices, and other files shared with you.</p>

      {msg && <StatusMessage tone="err">{msg}</StatusMessage>}

      {documents.length === 0 ? (
        <p className="muted">No documents have been shared with you yet.</p>
      ) : (
        <div className="table-wrap card" style={{ padding: 0 }}>
          <table>
            <caption className="visually-hidden">Documents shared with you</caption>
            <thead>
              <tr>
                <th scope="col">Name</th>
                <th scope="col">Type</th>
                <th scope="col">Added</th>
                <th scope="col">Download</th>
              </tr>
            </thead>
            <tbody>
              {documents.map((d) => (
                <tr key={d.id}>
                  <td>{d.title ?? d.filename}</td>
                  <td>{d.retentionClass.replace(/_/g, ' ')}</td>
                  <td>{formatDate(d.createdAt)}</td>
                  <td>
                    <button type="button" className="secondary" onClick={() => download(d.id)}>
                      Download<span className="visually-hidden"> {d.title ?? d.filename}</span>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

export function Documents() {
  const query = useQuery({
    queryKey: ['portal', 'documents'],
    queryFn: () => api.portal.documents(),
  });
  if (query.isLoading) return <Loading label="Loading your documents…" />;
  if (query.isError || !query.data)
    return <ErrorState message={errorMessage(query.error)} onRetry={() => query.refetch()} />;
  return <DocumentsView documents={query.data.documents} />;
}
